/*
  Tearstones homepage stone discovery

  Progressively enhances stone categories with accessible tabs and optional rail
  controls. Product links, images and commercial data remain owned by Liquid.
  Phone layouts expose every item in the selected category without a carousel.
*/
class TearstonesHomeShapeRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('.ts-home-shapes-rail');
    this.controls = this.querySelector('.ts-home-shapes-controls');
    this.previous = this.querySelector('[data-shape-prev]');
    this.next = this.querySelector('[data-shape-next]');
    if (!this.rail || !this.controls || !this.previous || !this.next || this.events) return;
    this.events = new AbortController();
    const on = (element, type, handler) => {
      element.addEventListener(type, handler, { signal: this.events.signal });
    };
    const update = () => this.refresh();
    const move = (direction) => {
      const rtl = getComputedStyle(this.rail).direction === 'rtl';
      const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rail.scrollBy({
        left: direction * (rtl ? -1 : 1) * this.rail.clientWidth * 0.85,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    };
    on(this.previous, 'click', () => move(-1));
    on(this.next, 'click', () => move(1));
    on(this.rail, 'scroll', update);
    on(window, 'resize', update);
    on(window, 'pageshow', update);
    on(this.rail, 'keydown', (event) => {
      if (event.target !== this.rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (this.rail.scrollWidth - this.rail.clientWidth < 2) return;
      event.preventDefault();
      const rtl = getComputedStyle(this.rail).direction === 'rtl';
      move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
    });
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(update);
      this.observer.observe(this.rail);
    }
    update();
  }

  refresh() {
    if (!this.rail || !this.controls || !this.previous || !this.next) return;
    const limit = this.rail.scrollWidth - this.rail.clientWidth;
    const position = Math.abs(this.rail.scrollLeft);
    const scrollable = this.rail.clientWidth > 0 && limit >= 2;
    this.controls.hidden = !scrollable;
    this.rail.tabIndex = scrollable ? 0 : -1;
    this.previous.disabled = !scrollable || position < 2;
    this.next.disabled = !scrollable || position >= limit - 2;
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    this.observer?.disconnect();
  }
}

if (!customElements.get('tearstones-home-shape-rail')) {
  customElements.define('tearstones-home-shape-rail', TearstonesHomeShapeRail);
}

class TearstonesHomeStoneTabs extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.nav = this.querySelector('[data-tabs-nav]') || this.querySelector('.ts-home-stone-tabs-nav');
    if (!this.nav) return;
    const panels = Array.from(this.querySelectorAll('[data-stone-panel]'));
    const tabs = Array.from(this.nav.querySelectorAll('button[data-stone-tab]'));
    this.items = tabs.map((tab) => ({
      tab,
      panel: panels.find((panel) => panel.id && panel.id === tab.getAttribute('aria-controls')),
    }));
    const matchedPanels = new Set(this.items.map(({ panel }) => panel).filter(Boolean));
    if (tabs.length < 2 || matchedPanels.size !== tabs.length || matchedPanels.size !== panels.length) return;

    this.events = new AbortController();
    this.originalStates = [];
    this.remember(this.nav, ['hidden', 'role', 'aria-orientation']);
    this.nav.setAttribute('role', 'tablist');
    this.nav.setAttribute('aria-orientation', 'horizontal');
    this.items.forEach(({ tab, panel }, index) => {
      this.remember(tab, ['id', 'type', 'role', 'tabindex', 'aria-selected']);
      this.remember(panel, ['hidden', 'role', 'aria-labelledby']);
      if (!tab.id) tab.id = `${panel.id}-tab`;
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      const title = panel.querySelector('[data-stone-panel-title]');
      if (title) {
        this.remember(title, ['hidden']);
        title.hidden = true;
      }
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        this.select(index, true);
      }, { signal: this.events.signal });
      tab.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        let next = index;
        const rtl = getComputedStyle(this.nav).direction === 'rtl';
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.items.length - 1;
        else if (event.key === 'ArrowLeft') next += rtl ? 1 : -1;
        else if (event.key === 'ArrowRight') next += rtl ? -1 : 1;
        else return;
        event.preventDefault();
        this.select((next + this.items.length) % this.items.length, true);
      }, { signal: this.events.signal });
    });
    this.select(0);
    this.nav.hidden = false;
  }

  remember(element, names) {
    this.originalStates.push({ element, attributes: names.map((name) => [name, element.getAttribute(name)]) });
  }

  select(index, focus = false) {
    const selected = this.items[index];
    if (!selected) return;
    this.items.forEach(({ tab, panel }, itemIndex) => {
      const active = itemIndex === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      panel.hidden = !active;
    });
    selected.panel.querySelectorAll('tearstones-home-shape-rail').forEach((rail) => rail.refresh?.());
    if (focus) selected.tab.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    this.originalStates?.forEach(({ element, attributes }) => {
      attributes.forEach(([name, value]) => {
        if (value === null) element.removeAttribute(name);
        else element.setAttribute(name, value);
      });
    });
    this.originalStates = null;
  }
}

if (!customElements.get('tearstones-home-stone-tabs')) {
  customElements.define('tearstones-home-stone-tabs', TearstonesHomeStoneTabs);
}

/* Native collection sorting supplies each feed, independently of its default order. */
class TearstonesHomeProductTabs extends TearstonesHomeStoneTabs {
  connectedCallback() {
    super.connectedCallback();
    if (!this.events || this.requests) return;
    this.requests = new Map();
    const onImage = (event) => {
      const image = event.target;
      if (!image.matches?.('[data-feed-grid] img')) return;
      const media = image.closest('.ts-home-featured-image');
      if (!media) return;
      media.removeAttribute('data-media-loading');
      media.toggleAttribute('data-media-error', event.type === 'error');
    };
    this.addEventListener('load', onImage, { capture: true, signal: this.events.signal });
    this.addEventListener('error', onImage, { capture: true, signal: this.events.signal });
    this.items.forEach(({ panel }) => this.prepareImages(panel.querySelector('[data-feed-grid]')));
    const loadActive = () => this.items.forEach(({ tab, panel }) => {
      if (tab.getAttribute('aria-selected') === 'true') this.load(panel);
    });
    if ('IntersectionObserver' in window) {
      this.feedObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.feedObserver.disconnect();
        loadActive();
      }, { rootMargin: '200px' });
      this.feedObserver.observe(this);
    } else loadActive();
  }

  select(index, focus = false) {
    super.select(index, focus);
    if (this.requests) this.load(this.items[index]?.panel);
  }

  async load(panel) {
    if (!panel || panel.dataset.feedLoaded || this.requests.has(panel)) return;
    const grid = panel.querySelector('[data-feed-grid]');
    const status = panel.querySelector('[data-feed-status]');
    if (!grid || !status) return;
    const controller = new AbortController();
    this.requests.set(panel, controller);
    panel.setAttribute('aria-busy', 'true');
    panel.dataset.feedState = 'loading';
    status.textContent = status.dataset.loading;
    status.classList.add('visually-hidden');
    status.hidden = false;
    try {
      const url = new URL(panel.dataset.feedUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Unexpected collection origin');
      const current = new URL(window.location.href);
      if (current.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', current.searchParams.get('preview_theme_id'));
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Collection request failed');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const template = document.querySelector('template[data-home-jewelry-cards]');
      if (!template || template.dataset.collectionId !== this.dataset.collectionId || !template.content.querySelector('[data-home-product-id]')) throw new Error('Collection feed unavailable');
      if (!this.isConnected || controller.signal.aborted) return;
      grid.replaceChildren(template.content.cloneNode(true));
      this.prepareImages(grid);
      panel.dataset.feedLoaded = 'true';
      panel.dataset.feedState = 'ready';
      status.hidden = true;
    } catch (error) {
      if (controller.signal.aborted || !this.isConnected) return;
      this.prepareImages(grid);
      panel.dataset.feedState = 'error';
      status.textContent = status.dataset.error;
      status.classList.remove('visually-hidden');
    } finally {
      if (this.requests?.get(panel) === controller) {
        panel.removeAttribute('aria-busy');
        this.requests.delete(panel);
      }
    }
  }

  prepareImages(grid) {
    grid?.querySelectorAll('.ts-home-featured-image').forEach((media) => {
      const image = media.querySelector('img');
      media.toggleAttribute('data-media-loading', Boolean(image && !image.complete));
      media.toggleAttribute('data-media-error', Boolean(image?.complete && !image.naturalWidth));
    });
  }

  disconnectedCallback() {
    this.feedObserver?.disconnect();
    this.requests?.forEach((controller, panel) => {
      controller.abort();
      panel.removeAttribute('aria-busy');
      if (!panel.dataset.feedLoaded) panel.dataset.feedState = 'pending';
    });
    this.requests = null;
    super.disconnectedCallback();
  }
}
if (!customElements.get('tearstones-home-product-tabs')) {
  customElements.define('tearstones-home-product-tabs', TearstonesHomeProductTabs);
}
